import * as THREE from 'three';
import { CAMERA, HEADBOB, IS_DEV } from '../config';
import { BALLISTICS } from './ballistics';
import { terrainHeight } from './terrain';

const DEG = Math.PI / 180;

// Isometric-angled camera with free orbit. It uses a perspective projection so the world has
// real depth, but "spatial compression" is controlled by a dolly-zoom: the field of view sets
// how much perspective/depth there is, while the distance is recomputed to keep the framed
// area (subject size) constant. Small fov = flat/compressed (near-orthographic); larger fov =
// more depth. Zoom changes the framing; orbiting never changes the player's travel direction.
export class IsoCamera {
  readonly camera: THREE.PerspectiveCamera;
  readonly focus = new THREE.Vector3(0, 0, 0);
  azimuth: number = CAMERA.startAzimuth;
  polar: number = CAMERA.startPolar;
  zoom: number = CAMERA.startZoom;
  firstPerson = false; // true once you scroll in past max zoom (into the player's eyes)
  private fpPitch = 0; // first-person look pitch (0 = horizon)
  private shake = 0; // decorative trauma shake (decays); a POSITION-only jitter so it never moves the aim
  private fpFov: number = CAMERA.fpFovDefault;
  private fovDeg: number = CAMERA.fovDeg;
  private sprintFovT = 0; // 0..1 eased sprint-FOV-kick amount (chases sprinting each frame in update())
  private bobAmpT = 0; // eased 0..1 head-bob amplitude (ramps to 0 when idle/aiming/3rd person, else toward 1)
  // 3RD-PERSON recoil offset: per shot kicks the TARGET (rTgtP/Y); the APPLIED offset (recoilPitch/Yaw) chases
  // the target SMOOTHLY each frame. The camera itself never moves -- main rotates the AIM RAY up by this offset
  // (and the reticle marks where bullets will land), so the player fights the drift 1:1 with the mouse, exactly
  // like 1st person. (1st person doesn't use this at all: its recoil is baked into fpPitch/azimuth via addLookKick.)
  recoilPitch = 0; recoilYaw = 0;
  private rTgtP = 0; private rTgtY = 0;

  constructor(aspect: number) {
    this.camera = new THREE.PerspectiveCamera(this.fovDeg, aspect, CAMERA.near, CAMERA.far);
  }

  resize(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  orbit(dxPixels: number, dyPixels: number): void {
    this.azimuth -= dxPixels * CAMERA.orbitYawPerPixel;
    if (this.firstPerson) {
      this.fpPitch = clamp(this.fpPitch - dyPixels * CAMERA.orbitPitchPerPixel, -CAMERA.fpPitchLimit, CAMERA.fpPitchLimit);
    } else {
      this.polar = clamp(this.polar - dyPixels * CAMERA.orbitPitchPerPixel, CAMERA.minPolar, CAMERA.maxPolar);
    }
  }

  // Zoom-out floor: full (0.12) in dev/localhost, capped (minZoomProd) in production so live players can pull
  // back for awareness but not scout the whole map from above.
  private get minZoomLimit(): number { return IS_DEV ? CAMERA.minZoom : CAMERA.minZoomProd; }

  // Third-person zoom ONLY. The wheel no longer enters/leaves first person -- that is the V toggle's job
  // (owner: "always use the button to toggle views"). In first person the wheel is intercepted upstream to
  // cycle the hotbar, so this never runs there; the guard is just belt-and-suspenders.
  // F8 gun-fit tuning wants to get CLOSER than the normal gameplay clamp to inspect the held model.
  tuneZoomBoost = false; // set by main while the F8 overlay is open
  zoomBy(wheelDelta: number): void {
    if (this.firstPerson) return;
    const factor = 1 - wheelDelta * CAMERA.zoomPerWheel;
    this.zoom = clamp(this.zoom * factor, this.minZoomLimit, CAMERA.maxZoom * (this.tuneZoomBoost ? 1.8 : 1));
  }

  // Flip first person on/off (V key) -- the ONLY way to switch views now. Third-person zoom is preserved
  // across the toggle (we don't reset it), so V-ing out returns you to the distance you had.
  setFirstPerson(v: boolean): void {
    if (v === this.firstPerson) return;
    this.firstPerson = v;
    if (v) this.fpPitch = 0; // reset look pitch on entry
  }

  toggleFirstPerson(): void {
    this.setFirstPerson(!this.firstPerson);
  }

  // 3RD-PERSON: kick the aim-offset TARGET by (pitch, yaw) radians (called per shot). +pitch climbs the aim up.
  // Capped LOW -- the cap IS the spray plateau: sustained auto parks the aim marker recoil3pPitchCap above the
  // cursor and holds there, fightable 1:1 with the mouse; it can never ratchet off to the sky.
  addRecoil(pitch: number, yaw: number): void {
    const pCap = BALLISTICS.recoil3pPitchCap * DEG, yCap = BALLISTICS.recoil3pYawCap * DEG;
    this.rTgtP = clamp(this.rTgtP + pitch, -pCap, pCap);
    this.rTgtY = clamp(this.rTgtY + yaw, -yCap, yCap);
  }
  // FIRST-PERSON recoil: kick the actual look (pitch up + a little yaw) PERMANENTLY -- NO auto-recovery. The
  // player owns the view and pulls the mouse down to fight it; we never snap their aim back for them.
  addLookKick(pitch: number, yaw: number): void {
    this.fpPitch = clamp(this.fpPitch + pitch, -CAMERA.fpPitchLimit, CAMERA.fpPitchLimit);
    this.azimuth += yaw;
  }
  // Each frame: ease the target home -- at the full recover rate between bursts, at a slow drain even MID-burst
  // (so the offset always settles back and never sticks as a permanent invisible skew); ALWAYS chase the applied
  // offset toward the target smoothly (so the climb is continuous, not a per-shot jolt). Owns its knobs: the
  // call site just passes dt + the firing flag.
  tickRecoil(dt: number, firing: boolean): void {
    const d = (firing ? BALLISTICS.recoil3pBurstRecover : BALLISTICS.recoilRecover) * DEG * dt;
    this.rTgtP = this.rTgtP > 0 ? Math.max(0, this.rTgtP - d) : Math.min(0, this.rTgtP + d);
    this.rTgtY = this.rTgtY > 0 ? Math.max(0, this.rTgtY - d) : Math.min(0, this.rTgtY + d);
    const k = 1 - Math.exp(-dt * 14); // smoothing: reach the target in ~0.1s
    this.recoilPitch += (this.rTgtP - this.recoilPitch) * k;
    this.recoilYaw += (this.rTgtY - this.recoilYaw) * k;
  }
  // bypassFloor: the SCOPE needs to go well below the settings slider's fpFovMin (a real sniper optic is
  // much tighter than the tightest normal FOV setting) -- only the scope call site passes true.
  setFpFov(fov: number, bypassFloor = false): void {
    this.fpFov = bypassFloor ? clamp(fov, 2, CAMERA.fpFovMax) : clamp(fov, CAMERA.fpFovMin, CAMERA.fpFovMax);
    if (this.firstPerson) { this.camera.fov = this.fpFov; this.camera.updateProjectionMatrix(); }
  }
  // Enter first person ORIENTED to an exact world-space look direction (owner ask: the 3rd-person sniper
  // scope must open already centred on whatever the free cursor was aiming at, not wherever the orbit
  // camera's azimuth/pitch happened to be facing). Bypasses setFirstPerson's pitch-reset-to-0.
  enterFirstPersonLookingAt(dir: THREE.Vector3): void {
    this.firstPerson = true;
    this.fpPitch = clamp(Math.asin(clamp(dir.y, -1, 1)), -CAMERA.fpPitchLimit, CAMERA.fpPitchLimit);
    this.azimuth = Math.atan2(-dir.x, -dir.z); // matches the FP forward formula in update() below
  }

  // Spatial compression. Lower = flatter (less perspective), higher = more depth. Framing is
  // preserved because update() recomputes the distance from this fov.
  setCompression(fovDeg: number): void {
    this.fovDeg = clamp(fovDeg, CAMERA.fovMin, CAMERA.fovMax);
    this.camera.fov = this.fovDeg;
    this.camera.updateProjectionMatrix();
  }

  get compression(): number {
    return this.fovDeg;
  }

  // A decorative impact shake (gunfire/explosions). POSITION-only + applied after lookAt, so the view jitters but
  // the aim direction is untouched. Accumulates + is capped; decays each frame in update().
  punch(amount: number): void { this.shake = Math.min(1, this.shake + amount * 0.5); }

  // THIRD-PERSON aim framing (owner: bow draws need feel; extended to gun ADS in the aim overhaul):
  // while a bow/spear is drawn OR a gun is sighted the camera eases ~12% closer AND slides to an
  // over-the-shoulder offset (the character stops occluding the target), snapping back on release.
  // FP has its own FOV zoom via updateScope; this only shapes the 3P framing (see update()'s halfH).
  private aimZoomT = 0;
  private aimZoomTarget = 0;
  setAimZoom(t: number): void { this.aimZoomTarget = Math.max(0, Math.min(1, t)); }

  // Jitter the camera position by the current shake, then decay it. Called after lookAt so orientation (aim) holds.
  private applyShake(): void {
    if (this.shake <= 0.001) { this.shake = 0; return; }
    const s = this.shake * 0.06;
    this.camera.position.x += (Math.random() - 0.5) * s;
    this.camera.position.y += (Math.random() - 0.5) * s;
    this.camera.position.z += (Math.random() - 0.5) * s;
    this.shake *= 0.8;
  }

  // dt/sprinting drive the sprint-FOV kick (a smooth +sprintFovKick-degree widen while sprinting, eased
  // both ways so it never pops). bob = { phase, moving, sprinting, aiming } drives the first-person head-bob
  // (locked to the player's walkCyclePhase so it matches the visible footfalls); all optional so existing
  // callers/tests that don't care about either effect still work.
  update(target: THREE.Vector3, dt = 0, sprinting = false, bob?: { phase: number; moving: boolean; aiming: boolean }): void {
    this.focus.lerp(target, CAMERA.followLerp);
    const k = 1 - Math.exp(-dt * CAMERA.sprintFovEaseRate);
    this.sprintFovT += ((sprinting ? 1 : 0) - this.sprintFovT) * k;
    const fovKick = this.sprintFovT * CAMERA.sprintFovKick;

    // Head-bob amplitude: eases toward 0 when not moving / aiming / disabled, else toward 1, so a stop or
    // an ADS grab never snaps the bob off mid-cycle.
    const bobWanted = HEADBOB.enabled && !!bob && bob.moving && !bob.aiming;
    this.bobAmpT += ((bobWanted ? 1 : 0) - this.bobAmpT) * Math.min(1, dt * HEADBOB.easeRate);
    const bobAmp = (sprinting ? HEADBOB.sprintAmp : HEADBOB.walkAmp) * this.bobAmpT;
    const bobY = bob ? Math.sin(bob.phase) * bobAmp : 0;
    const bobX = bob ? Math.sin(bob.phase * 0.5) * HEADBOB.sideAmp * this.bobAmpT : 0;

    if (this.firstPerson) {
      // Sit in the player's eyes and look along the yaw (azimuth) + pitch. Forward matches the iso
      // look direction (toward where the camera used to sit), so movement stays consistent.
      const eyeY = this.focus.y + CAMERA.fpEyeOffset + bobY;
      // Side sway is along the camera's local right vector (perpendicular to azimuth), so it reads as a
      // lateral head sway regardless of look direction.
      const sideX = Math.cos(this.azimuth) * bobX, sideZ = -Math.sin(this.azimuth) * bobX;
      this.camera.position.set(this.focus.x + sideX, eyeY, this.focus.z + sideZ);
      const cp = Math.cos(this.fpPitch);
      this.camera.lookAt(
        this.focus.x + sideX - Math.sin(this.azimuth) * cp,
        eyeY + Math.sin(this.fpPitch),
        this.focus.z + sideZ - Math.cos(this.azimuth) * cp,
      );
      const targetFov = clamp(this.fpFov + fovKick, Math.min(CAMERA.fpFovMin, this.fpFov), CAMERA.fpFovMax + CAMERA.sprintFovKick);
      if (this.camera.fov !== targetFov) { this.camera.fov = targetFov; this.camera.updateProjectionMatrix(); }
      this.applyShake(); // decorative jitter (after lookAt = aim unaffected)
      return; // NOTE: FP recoil is baked into fpPitch/azimuth via addLookKick (permanent, player-managed) -- no auto-correct
    }
    const targetFov = this.fovDeg + fovKick;
    if (this.camera.fov !== targetFov) { this.camera.fov = targetFov; this.camera.updateProjectionMatrix(); }

    // Framed half-height at the focus (the orthographic-equivalent view size), divided by zoom.
    // Draw-zoom: ease in while a bow/spear is drawn (slow pull), release fast (the snap-back on fire).
    this.aimZoomT += (this.aimZoomTarget - this.aimZoomT) * (1 - Math.exp(-(this.aimZoomTarget > this.aimZoomT ? 5 : 16) * dt));
    const halfH = (CAMERA.frustum / this.zoom) * (1 - 0.12 * this.aimZoomT);
    // Dolly distance that makes that half-height fill the view at the current fov.
    const dist = halfH / Math.tan(THREE.MathUtils.degToRad(this.fovDeg) / 2);
    const cosP = Math.cos(this.polar);
    let cx = this.focus.x + dist * cosP * Math.sin(this.azimuth);
    let cy = this.focus.y + dist * Math.sin(this.polar);
    let cz = this.focus.z + dist * cosP * Math.cos(this.azimuth);
    // Keep the framing as it was, but never let the camera dip below the ground under it (no
    // clipping through the map). It is only ever pushed UP to clear terrain, never pulled in.
    const floor = terrainHeight(cx, cz) + CAMERA.terrainClearance;
    if (cy < floor) cy = floor;
    // Over-the-shoulder slide while sighting (aim overhaul item #6): shift camera AND focus to the
    // camera's right so the character clears the target line. Eases with the same aimZoomT as the zoom.
    const shoulder = AIM_SHOULDER * this.aimZoomT;
    let lx = this.focus.x, lz = this.focus.z;
    if (shoulder > 0.001) {
      const rx = Math.cos(this.azimuth), rz = -Math.sin(this.azimuth); // camera-right, horizontal
      cx += rx * shoulder; cz += rz * shoulder;
      lx += rx * shoulder; lz += rz * shoulder;
    }
    this.camera.position.set(cx, cy, cz);
    this.camera.lookAt(lx, this.focus.y, lz);
    this.applyShake(); // decorative jitter (after lookAt = aim/cursor mapping unaffected)
    // NOTE: no applyRecoil() in 3rd person -- the camera must NOT kick when shooting (it was disorienting).
    // The recoil climbs the SHOTS instead (offset on the fire direction in main), and you re-aim with the cursor.
  }
}

// Over-the-shoulder lateral slide (m) at full aim (aimZoomT = 1). Small: declutter, not a new camera.
const AIM_SHOULDER = 0.55;

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
